import {interpolate} from '../stringBundle';

export const TU_PARTNER_CODE = 'TU';
export const EXP_PARTNER_CODE = 'EXP';
export const CATU_PARTNER_CODE = 'CATU';
export const EFX_PARTNER_CODE = 'EFX';

export type LogDataObj = {
  feature: string;
  actionType?: string;
  message?: string;
  startTime: number;
  accountId?: string;
  clientId: string;
  statusCode?: number;
  error?: object | string;
};

/**
 * Returns Object of gaAction and text for Ga action based on topTabId and creditPullInfo
 * @param {Object} creditPullInfo -
 * @param {String} topTabId
 * @param {Object} localizedStringBundle
 * @returns {Object} it returns object of gaAction and text.
 */
export const getGaActionAndText = (topTabId: string, localizedStringBundle: StringMap) => {
  if (topTabId === TU_PARTNER_CODE || topTabId === EXP_PARTNER_CODE) {
    return {text: localizedStringBundle['3B_REPORT_MODAL_TEXT'], gaAction: 'New 3B Report'};
  }

  return {text: localizedStringBundle['1B_REPORT_MODAL_TEXT'], gaAction: 'New 1B Report'};
};

/**
 * Returns label and action type to trigger Ga event.
 * @param {Object} creditPullInfo -
 * @param {String} topTabId
 * @param {Object} localizedStringBundle
 * @param {String} primaryBureau
 * @returns {Array} it returns label and action for Ga event.
 */
export const getParamsForGaEvent = (topTabId: string, localizedStringBundle: StringMap, primaryBureau: string) => {
  const label =
    topTabId === getProvider(primaryBureau) ? `${topTabId} Get latest score & report` : `${topTabId} Confirm report`;
  const action =
    topTabId === getProvider(primaryBureau) ? 'click' : getGaActionAndText(topTabId, localizedStringBundle).gaAction;
  return [label, action];
};

/**
 * Returns frequencyText according to frequencyUnitType
 * @param {string} frequencyType - credit frequency type {Days, Months, Year}
 * @param {Object} localizedStringBundle - string bundle according to country code
 * @returns {string} it returns frequency string from localizedStringBundle
 */
export const getFrequencyText = (frequencyType: string, localizedStringBundle: StringMap) => {
  switch (frequencyType) {
    case 'Days':
      return localizedStringBundle.DAILY;
    case 'Months':
      return localizedStringBundle.MONTHLY;
    case 'Years':
      return localizedStringBundle.ANNUAL;
    default:
      return '';
  }
};

/**
 * Returns frequencyText according to frequencyUnitType
 * @param {string} frequencyText - credit frequency text {daily, monthly, annual}
 * @param {Object} localizedStringBundle - string bundle according to country code
 * @param {boolean} isTextForToolTip - boolean var to differentiate whether the text is for modal or tooltip
 * @returns {string} it returns feature detail from localizedStringBundle
 */
export const getFeatureDetail = (
  frequencyText: string,
  localizedStringBundle: StringMap,
  isTextForToolTip: boolean
) => {
  let featureDetailStr = '';

  if (isTextForToolTip) {
    featureDetailStr =
      frequencyText === 'annual'
        ? interpolate(localizedStringBundle.TU_CREDIT_TEXT2, {frequencyText})
        : interpolate(localizedStringBundle.TU_CREDIT_TEXT1, {frequencyText});
  } else {
    featureDetailStr =
      frequencyText === 'annual'
        ? interpolate(localizedStringBundle.TU_RETRIEVE_REPORT_TEXT2, {frequencyText})
        : interpolate(localizedStringBundle.TU_RETRIEVE_REPORT_TEXT1, {frequencyText});
  }
  return featureDetailStr;
};

/**
 * Returns the provider based on the bureau
 * @param bureau
 * @returns provider
 */
export const getProvider = (bureau = 'EFX') => {
  if (bureau === 'TU') {
    return 'CATU';
  }
  if (bureau === 'EXP') {
    return 'EXP';
  }
  return 'EFX';
};

/**
 * Returns the provider based on tabId and country
 * @param tabId - The tab ID (EFX, TU, or EXP)
 * @param country - The country code (e.g., 'CA')
 * @returns provider string
 */
export const getProviderByTabAndCountry = (tabId: string, country?: string) => {
  return tabId === TU_PARTNER_CODE && country === 'CA' ? 'CATU' : tabId;
};

/**
 * Returns the maximum score range for the arc chart based on the bureau/provider.
 * EFX , TU and EXP use VantageScore 3.0 with max 850.
 * CATU use CreditVision with max 900.
 *
 * @param tabId - The bureau/provider code ('EFX', 'EXP', 'TU', 'CATU')
 * @returns The maximum score as a string ('850', '900', or '')
 */
export const getArcChartMax = (tabId: string): string => {
  return [EFX_PARTNER_CODE, EXP_PARTNER_CODE, TU_PARTNER_CODE].includes(tabId)
    ? '850'
    : tabId === CATU_PARTNER_CODE
    ? '900'
    : '';
};

/**
 * Returns the score type based on the primary bureau and whether the user is a 3B user.
 * @param primaryBureau
 * @param is3BUser
 */
export const getScoreType = (primaryBureau = 'EFX', is3BUser = false) => {
  if (primaryBureau === 'TU') {
    return 'CA_1B';
  }
  return is3BUser ? 'US_3B' : 'US_EFX';
};

/**
 * Returns the report type based on the primary bureau and whether the user is a 3B user.
 * @param primaryBureau
 * @param is3BUser
 */
export const getReportType = (primaryBureau = 'EFX', is3BUser = false) => {
  return getScoreType(primaryBureau, is3BUser);
};
